/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Module, Mutation, Action } from "vuex-class-modules"
import { Modules } from "@/store/modules"
import { store } from "@/store/store"
import { AlertActions, alertModule } from "../alert.module"
import { apiService } from "@/services/api.service"
import { Endpoint } from "@/services/endpoints"
import { $t } from "@/plugins/i18n"
import { projectModule } from "../project.module"
import { FileMetaData } from "@/models/File"
import { UpdateObjectValue } from "@/models/common"
import { ServerResponse } from "@/models/ServerResponse"
import { AlertType, AlertContentItem } from "@/models/Alert"
import {
  AddItemEpicPayload,
  MoveTo,
  TaskType,
  ChangeItemOwner,
  ChangeItemStatus,
  Item,
  ItemDefinitionPyload,
  DefinitionOfDone,
  Status,
  DefinitionWithItemStatus,
  ItemStatus,
  ChangeTeamForItem,
  Team,
  ResponseToChangingItemTeam,
  ItemChangeValidationResultDto,
  ItemAnalytics,
  StructuredDescription,
  ItemPriorityParams,
  ItemValidateCheckPayload,
  Sprint,
  StatusChangeInitiator,
  typeCreateById,
  ItemType,
  itemTypesList,
  TeamType,
  ItemValidateCheckPayloadCreate,
  FilterBacklog,
} from "@/models"
import { BaseModule } from "@/models/BaseModule"
import { accountModule } from "../account.module"
import { parsingServerResponseContent } from "@/utils/helpers"
import { cutTextByNumberOfSymbols } from "@/helpers/cutTextByNumberOfSymbols"
import { sessionModule } from "../session.module"

export enum ItemActions {
  GET_ITEMS = "GET_ITEMS",
  GET_ITEM_SERVER_RESPONSE = "GET_ITEM_SERVER_RESPONSE",
  GET_ITEM = "GET_ITEM",
  POST_ITEM = "POST_ITEM",
  ITEM_VALIDATE_CREATE_CHECK = "ITEM_VALIDATE_CREATE_CHECK",
  ITEM_VALIDATE_CHANGE_CHECK = "ITEM_VALIDATE_CHANGE_CHECK",
  CHANGE_STATUS_ITEM = "CHANGE_STATUS_ITEM",
  RESET_ITEM = "RESET_ITEM",
  PUT_ITEM = "PUT_ITEM",
  UPDATE_ITEM_VALUE = "UPDATE_ITEM_VALUE",
  UPDATE_ITEM_VALUES = "UPDATE_ITEM_VALUES",
  GET_ITEM_FILES = "GET_ITEM_FILES",
  UPDATE_DEFINITION_OF_DONE = "UPDATE_DEFINITION_OF_DONE",
  ADD_ITEM_TO_EPIC = "ADD_ITEM_TO_EPIC",
  GET_ITEMS_SNAPSHOT_BY_EPIC_V2 = "GET_ITEMS_SNAPSHOT_BY_EPIC_V2",
  DELETE_ITEM = "DELETE_ITEM",
  DELETE_ITEM_FILE = "DELETE_ITEM_FILE",
  CHANGE_OWNER_ITEM = "CHANGE_OWNER_ITEM",
  MOVE_TO_ITEM = "MOVE_TO_ITEM",
  GET_ITEM_CLONE = "GET_ITEM_CLONE",
  UPDATE_ITEM_CLONE = "UPDATE_ITEM_CLONE",
  REMOVE_DEFINITION = "REMOVE_DEFINITION",
  GET_ITEMS_SEARCH = "GET_ITEMS_SEARCH",
  SET_OPENED_AVATAR_DROPDOWN_ID = "SET_OPENED_AVATAR_DROPDOWN_ID",
  GET_OPENED_AVATAR_DROPDOWN_ID = "GET_OPENED_AVATAR_DROPDOWN_ID",
  UPDATE_ITEM_DETAILS = "UPDATE_ITEM_DETAILS",
  UPDATE_TEAM = "UPDATE_TEAM",
  UPDATE_ITEM_TEAM_TYPE = "UPDATE_ITEM_TEAM_TYPE",
  CHANGE_TEAM_FOR_ITEM = "CHANGE_TEAM_FOR_ITEM",
  UPDATE_ITEM_STATUS_IN_LIST = "UPDATE_ITEM_STATUS_IN_LIST",
  UPDATE_ITEM_IN_LIST = "UPDATE_ITEM_IN_LIST",
  GET_BACKLOG_CREATED_ITEM = "GET_BACKLOG_CREATED_ITEM",
  UPDATE_VALUE_FOR_BACKLOG_CREATED_ITEM = "UPDATE_VALUE_FOR_BACKLOG_CREATED_ITEM",
  RESET_CLONE = "RESET_CLONE",
  REMOVE_FILE_FROM_ITEM = "REMOVE_FILE_FROM_ITEM",
  UPDATE_ITEM_READINESS = "UPDATE_ITEM_READINESS",
  CHANGE_TEAM_FOR_ITEMS_BATCH = "CHANGE_TEAM_FOR_ITEMS_BATCH",
  SET_ITEM = "SET_ITEM",
  GET_ITEM_ANALYTICS = "GET_ITEM_ANALYTICS",
  GET_ITEM_PRIORITY = "GET_ITEM_PRIORITY",
  UPDATE_ITEM_CLONE_VALUE= "UPDATE_ITEM_CLONE_VALUE",
  GET_AVAILABLE_ITEM_TYPES = "GET_AVAILABLE_ITEM_TYPES",
  GET_ITEM_TEAM_TYPE = "GET_ITEM_TEAM_TYPE",
  GET_ITEM_TYPES_ID = "GET_ITEM_TYPES_ID",
  GET_SORTED_ITEM_TYPES = "GET_SORTED_ITEM_TYPES",
  GET_SORTED_ITEM_TYPES_WITHOUT_SPIKE = "GET_SORTED_ITEM_TYPES_WITHOUT_SPIKE",
  DUPLICATE_ITEM = "DUPLICATE_ITEM",
  GET_ITEM_TYPES_BY_TEAM = "GET_ITEM_TYPES_BY_TEAM",
}

@Module
class ItemModule extends BaseModule {
  items: Item[] = []
  itemServerResponse: ServerResponse<Item>
  item: Item = new Item()
  itemClone: Item = new Item()
  openedAvatarDropdownId: string | null = null
  backlogCreatedItem: Item | null = null
  itemAnalytics: ItemAnalytics = new ItemAnalytics()
  sortedItemTypes: ItemType[] = [
    "STORY",
    "SPIKE",
    "BUG",
    "TECH_DEBT",
    "DESIGN_DEBT",
    "SUPPORT_CASE",
    "DEV",
    "DEV_OPS",
    "OPS",
    "INCIDENT"
  ]

  get [`get/${ItemActions.GET_ITEM_TEAM_TYPE}`]() {
    return this.item.teamType ?? this.item.team?.type ?? sessionModule.team?.type ?? "DEVELOPMENT"
  }

  get [`get/${ItemActions.GET_AVAILABLE_ITEM_TYPES}`]() {
    const teamType = this[`get/${ItemActions.GET_ITEM_TEAM_TYPE}`]
    const availableItemTypes = sessionModule.itemConfig[teamType].availableItemTypes
    return itemTypesList.filter(itemType => availableItemTypes.includes(itemType)).map(itemType => typeCreateById({ id: itemType }))
  }

  get [`get/${ItemActions.GET_ITEM_TYPES_BY_TEAM}`]() {
    return (teamType?: TeamType) => {
      let itemTypes: ItemType[] = [...itemTypesList]
      if(teamType) {
        const availableItemTypes = sessionModule.itemConfig[teamType]?.availableItemTypes ?? []
        itemTypes = availableItemTypes.length ? 
          itemTypesList.filter(itemType => availableItemTypes.includes(itemType)) : 
          [...itemTypesList]
      }
      return itemTypes.map(itemType => typeCreateById({ id: itemType }))
    }
  }

  get [`get/${ItemActions.GET_ITEM_TYPES_ID}`]() {
    const teamType = this[`get/${ItemActions.GET_ITEM_TEAM_TYPE}`]
    return sessionModule.itemConfig[teamType].availableItemTypes
  }

  get [`get/${ItemActions.GET_OPENED_AVATAR_DROPDOWN_ID}`]() {
    return this.openedAvatarDropdownId
  }

  get accountId() {
    return accountModule.accountId
  }

  get projectId() {
    return projectModule.currentProjectId
  }

  get [`get/${ItemActions.GET_ITEMS}`]() {
    return this.items
  }

  get [`get/${ItemActions.GET_ITEM}`]() {
    return this.item
  }

  get [`get/${ItemActions.GET_ITEM_CLONE}`]() {
    return this.itemClone
  }

  get [`get/${ItemActions.GET_BACKLOG_CREATED_ITEM}`]() {
    return this.backlogCreatedItem
  }

  get [`get/${ItemActions.GET_ITEM_SERVER_RESPONSE}`]() {
    return this.itemServerResponse
  }

  get [`get/${ItemActions.GET_ITEM_ANALYTICS}`]() {
    return this.itemAnalytics
  }

  get [`get/${ItemActions.GET_SORTED_ITEM_TYPES}`]() {
    const availableItemTypes = this[`get/${ItemActions.GET_ITEM_TYPES_ID}`]
    return this.sortedItemTypes.filter(itemType => availableItemTypes.includes(itemType))
  }

  get [`get/${ItemActions.GET_SORTED_ITEM_TYPES_WITHOUT_SPIKE}`]() {
    return this[`get/${ItemActions.GET_SORTED_ITEM_TYPES}`].filter(type => type !== "SPIKE")
  }
  

  @Mutation
  setItemAnalytics(itemAnalytics: ItemAnalytics) {
    this.itemAnalytics = itemAnalytics
  }

  @Mutation
  setOpenedAvatarDropdownId(id: string | null) {
    this.openedAvatarDropdownId = id
  }

  @Mutation
  setItems(itemServerResponse: ServerResponse<Item>) {
    this.items = parsingServerResponseContent({
      originalContent: this.items,
      serverResponseContent: itemServerResponse.content,
      numberOfPage: itemServerResponse.number,
    })
    this.itemServerResponse = { ...itemServerResponse, content: this.items }
  }

  @Mutation
  replaceItem(item: Item) {
    this.items = this.items.map(i => item.id === i.id ? item : i)
  }

  @Mutation
  setItemClone(item: Item) {
    this.itemClone = Item.create(item)
  }

  @Mutation
  setItem(item: Item) {
    this.item = item
  }

  @Mutation
  // update progress fields in Item
  setProgress(item: Item) {
    const listItem = this.items.find(el => el.id === item.id)
    const updateFields = [
      "tasks",
      "status",
      "statusParsed",
      "definitionsOfDone",
    ]
    const updateItem = Item.create(item)
    updateFields.forEach((updateField) => {
      if (listItem) {
        listItem[updateField] = updateItem[updateField]
      }
      this.item[updateField] = updateItem[updateField]
      this.itemClone[updateField] = updateItem[updateField]
    })
  }

  @Mutation
  setItemValue(payload: UpdateObjectValue<Item>) {
    this.item.onValueUpdate(payload)
    Object.assign(this.item, { [payload["id"]]: payload["value"] })
  }
  
  @Mutation
  setItemCloneValue(payload: UpdateObjectValue<Item>) {
    Object.assign(this.itemClone, { [payload["id"]]: payload["value"] })
  }

  @Mutation
  setItemValues({ payload, updateClone } : { payload: UpdateObjectValue<Item>[], updateClone?: boolean }) {
    payload.forEach((e) => {
      this.item.onValueUpdate(e)
      Object.assign(this.item, { [e["id"]]: e["value"] })
      if (updateClone) {
        Object.assign(this.itemClone, { [e["id"]]: e["value"] })
      }
    })
  }

  @Mutation
  updateItemClone(itemField: string) {
    this.itemClone[itemField] = this.item[itemField]
  }

  @Mutation
  removeFileFromItem(fileMetadataId: string) {
    this.item.fileMetadata = this.item.fileMetadata?.filter(file => file.id !== fileMetadataId)
    this.item.attachmentIds = this.item.attachmentIds?.filter(id => id !== fileMetadataId)
    this.item.tasks.forEach((task) => {
      task.fileMetadataIds = task.fileMetadataIds?.filter(fileId => fileId !== fileMetadataId)
    })
  }

  @Mutation
  setStatusToItemList({ status, itemId } : {status: ItemStatus, itemId: string}) {
    const statusParsed = Status.createById({
      id: status ?? "NOT_STARTED",
    })
    this.items = this.items.map((item) => {
      if (item.id === itemId) Object.assign(item, { status, statusParsed })
      return item
    })
  }

  @Mutation
  setStatusAndDefinition(payload: DefinitionWithItemStatus) {
    const definitionsOfDone = payload.definitionsOfDone.map(definition => DefinitionOfDone.create(definition))
    const status = payload.status
    const statusParsed = Status.createById({
      id: status ?? "NOT_STARTED",
    })
    const estimation = payload.estimation
    Object.assign(this.item, {
      status,
      statusParsed,
      definitionsOfDone,
      estimation,
    })
    Object.assign(this.itemClone, {
      status,
      statusParsed,
      definitionsOfDone,
      estimation,
    })
  }

  @Mutation
setStatusToItem(value: StatusChangeInitiator) {
  const { initiator, status, description } = value;


  const statusParsed = Status.createById({
    id: status ?? "NOT_STARTED",
  });


  Object.assign(this.item, {
    status,
    statusParsed,
    statusChangeDescription: new StructuredDescription(description),
    initiator: initiator
  });

  Object.assign(this.itemClone, {
    status,
    statusParsed,
    statusChangeDescription: new StructuredDescription(description),
    initiator
  });
}

  @Mutation
  resetItem() {
    this.item = new Item()
    this.itemClone = new Item()
  }

  @Mutation
  removeDefinition(taskType: TaskType) {
    this.item.definitionsOfDone = this.item.definitionsOfDone.filter(def => def.type !== taskType)
  }

  @Mutation
  setItemInList({ payload, itemId }: { payload: UpdateObjectValue<Item>, itemId: string}) {
    this.items = this.items.map((el) => {
      if (el.id === itemId) el[payload?.["id"]] = payload?.["value"]
      return el
    })
  }

  @Mutation
  setbacklogCreatedItem(backlogCreatedItem: Item | null) {
    this.backlogCreatedItem = backlogCreatedItem ? Item.create(backlogCreatedItem) : null
  }

  @Mutation
  resetItemClone() {
    this.itemClone = Item.create(this.item)
  }

  @Mutation
  setValueForBacklogCreatedItem(params: UpdateObjectValue<Item>) {
    if (!this.backlogCreatedItem) return
    Object.assign(this.backlogCreatedItem, { [params["id"]]: params["value"] })
  }

  private getChangeTeamAlert(payload: { items: Item[], team: Team | null, moveTo?: string }) {
    const item = payload.items[0]
    const team = payload.team

    if (!item) {
      return []
    }

    let alertContent: AlertContentItem[] = []

    if (!payload.team) {
      // Item X - was unassigned from Team X and moved to a Product Backlog.

      alertContent = [
        {
          text: `${item.itemTypeParsed?.label ?? ""} `,
          type: "bold",
        },
        { text: `#${item.sequenceNumber}`, type: "bold" },
        { text: " was unassigned from ", type: "regular" },
        {
          text: `${item.team?.name ?? ""} `,
          type: "bold",
        },
        { text: " and moved to  ", type: "regular" },
        {
          text: `${Item.getBacklogNameByItem(item)}`,
          type: "regular",
        },
      ]
    }
    if (payload.moveTo === "unprioritizedBacklog") {
      alertContent = [
        {
          text: `${item.itemTypeParsed?.label ?? ""} `,
          type: "bold",
        },
        { text: `#${item.sequenceNumber}`, type: "bold" },
        { text: " was moved to ", type: "regular" },
        {
          text: `${item.team?.name ?? ""}`,
          type: "bold",
        },
        {
          text: " Team Unprioritized Backlog",
          type: "regular",
        },
      ]
    }
    if (payload.moveTo === "team") {
      if (payload.items.length > 1) {
        alertContent = [
          { text: "Items ", type: "bold" },
          { text: " were assigned to ", type: "regular" },
        ]
      } else {
        alertContent = [
          {
            text: `${item.itemTypeParsed?.label ?? ""} `,
            type: "bold",
          },
          {
            text: `#${item.sequenceNumber}`,
            type: "bold",
          },
          { text: " was assigned to ", type: "regular" },
        ]
      }
      alertContent.push(
        { text: `${team?.name ?? ""} `, type: "bold" },
        { text: "Team", type: "regular" },
      )
    }
    return alertContent
  }

  private getItemUpdateAlertContent(item: Item) {
    let alertContent: AlertContentItem[] = []

    if (item.hasChangedProp("itemTypeParsed")) {
      // "[Original ITEM TYPE] #123 was changed to a [New Item Type]" // for story and spike
      // "[Original ITEM TYPE] #123 was updated to [New Item Type] and moved to Unprioritized Backlog" // type
      // "[Original ITEM TYPE] #123 was updated to [New Item Type] and moved to [TEAM NAME] Team Unprioritized Backlog" // type and team

      const oldType = item.oldValueForProp("itemTypeParsed")?.id
      const newType = item.itemTypeParsed?.id
      const inEpic = !!item?.epic && !item.team
      const hasItemChangedFromStoryToSpike = [
        oldType,
        newType,
      ].every(type => [
        "STORY",
        "SPIKE",
      ].includes(type))
      const inSprint = !!item?.sprint
      // if item type was changed from story to spike or back
      if (hasItemChangedFromStoryToSpike || inSprint || inEpic) {
        alertContent = [
          {
            text: `${item.oldValueForProp("itemTypeParsed")?.label ?? ""}`,
            type: "bold",
          },
          {
            text: ` #${item.sequenceNumber}`,
            type: "bold",
          },
          { text: " was changed to ", type: "regular" },
          {
            text: `${item.itemTypeParsed?.label ?? ""}`,
            type: "bold",
          },
        ]
      } else {
        alertContent = [
          {
            text: `${item.oldValueForProp("itemTypeParsed")?.label ?? ""}`,
            type: "bold",
          },
          {
            text: ` #${item.sequenceNumber}`,
            type: "bold",
          },
          { text: " was updated to ", type: "regular" },
          {
            text: `${item.itemTypeParsed?.label ?? ""}`,
            type: "bold",
          },
          { text: " and moved to ", type: "regular" },
          {
            text: `${item.hasChangedProp("team") ? item.team?.name ?? "" : ""}`,
            type: "bold",
          },
          {
            text: `${item.hasChangedProp("team") ? " Team " : ""}Unprioritized Backlog`,
            type: "regular",
          },
        ]
      }
    } else if (item.hasChangedProp("team")) {
      // "[ITEM TYPE] #123 was updated and moved to [TEAM NAME] Team Unprioritized Backlog" // team and changes
      // "[ITEM TYPE] #123 was moved to [TEAM NAME] Team Unprioritized Backlog" // team
      alertContent = [
        {
          text: `${item.itemTypeParsed?.label ?? ""} `,
          type: "bold",
        },
        { text: `#${item.sequenceNumber}`, type: "bold" },
        {
          text: ` was ${(Object.keys(item.changedFieldsOriginValues).length > 1) ? "updated and " : " "}moved to `,
          type: "regular",
        },
        { text: `${item.team?.name ?? ""}`, type: "bold" },
        {
          text: " Team Unprioritized Backlog",
          type: "regular",
        },
      ]
    } else {
      // "[ITEM TYPE] #123 updated" // any other updates
      alertContent = [
        {
          text: `${item.itemTypeParsed?.label ?? ""} `,
          type: "bold",
        },
        { text: `#${item.sequenceNumber}`, type: "bold" },
        { text: " updated", type: "regular" },
      ]
    }

    return alertContent
  }


  @Action
  [ItemActions.SET_OPENED_AVATAR_DROPDOWN_ID](id: string | null) {
    this.setOpenedAvatarDropdownId(id)
  }

  @Action
  async [ItemActions.GET_ITEMS]() {
    const data = await apiService.get<ServerResponse<Item>>(Endpoint.GET_ITEMS(this.projectId))
    return this.handleResponse<ServerResponse<Item>>(data, (data) => {
      data.content = data.content.map(i => Item.create(i))
      this.setItems(data)
      return data
    })
  }

  @Action
  async [ItemActions.GET_ITEMS_SEARCH](filter) {
    const data = await apiService.get<ServerResponse<Item>>(Endpoint.BACKLOG(this.projectId), filter.getJsonObj())
    return this.handleResponse<ServerResponse<Item>>(data, (data) => {
      data.content = data.content.map(i => Item.create(i))
      this.setItems(data)
      return data
    })
  }

  @Action
  async [ItemActions.GET_ITEM]({ itemId, projectId, onlyGet } : { itemId: string, projectId?: string, onlyGet?: boolean }) {
    const selectedProject = projectId ? projectId : this.projectId
    const data = await apiService.get<Item>(Endpoint.GET_ITEM_PROTECTED(this.accountId, selectedProject, itemId))
    return this.handleResponse<Item>(data, (data) => {
      const item = Item.create(data)
      if (!onlyGet) {
        this.setItemClone(item)
        this.setItem(item)
        this.item.setInitialState()
      }
      return item
    }, true,
      (data) => {
        return data
      })
  }

  @Action
  async [ItemActions.GET_ITEM_PRIORITY]({ itemId, projectId, onlyGet } : { itemId: string, projectId?: string, onlyGet?: boolean }) {
    const selectedProject = projectId ? projectId : this.projectId
    const data = await apiService.get<ItemPriorityParams>(Endpoint.GET_ITEM_PRIORITY(selectedProject, itemId))
    return this.handleResponse<ItemPriorityParams>(data, (data) => {
      const { backlogPriority, sprintPriority, unprioritizedOrder, sprint } = data
      if (!onlyGet) {
        this.setItemValues({
          payload: [
            { id: "backlogPriority", value: backlogPriority ?? null },
            { id: "sprintPriority", value: sprintPriority ?? null },
            { id: "unprioritizedOrder", value: unprioritizedOrder ?? null },
            { id: "sprint", value: sprint && Object.keys(sprint).length ? new Sprint(sprint) : null },
          ] as any[],
          updateClone: true,
        })
      }
      return data
    })
  }

  @Action
  async [ItemActions.GET_ITEM_ANALYTICS]({ itemId, projectId, onlyGet } : { itemId: string, projectId?: string, onlyGet?: boolean }) {
    const selectedProject = projectId ? projectId : this.projectId
    const data = await apiService.get<ItemAnalytics>(Endpoint.GET_ITEM_ANALYTICS(this.accountId ,selectedProject, itemId))
    return this.handleResponse<ItemAnalytics>(data, (data) => {
      const itemAnalytics = new ItemAnalytics(data)
      if(!onlyGet) {
        this.setItemAnalytics(itemAnalytics)
      }
      return itemAnalytics
    })
  }

  @Action
  async [ItemActions.POST_ITEM]({ item, onlySend, alertMessage } : { item: Item, onlySend?: boolean, alertMessage?: AlertContentItem[] }) {
    const selectedProject = item.projectId ? item.projectId : this.projectId
    const payload = item.getJsonObj()
    const data = await apiService.post<Item>(Endpoint.POST_ITEM(selectedProject), payload)
    return this.handleResponse<Item>(data, (data) => {
      const newItem = Item.create(data)
      const backlogName = Item.getBacklogNameByItem(newItem)
      const alertContent = [
        {
          text: `${item.itemTypeParsed?.label ?? ""}`,
          type: "bold",
        } as AlertContentItem,
        {
          text: " was created and added to ",
          type: "regular",
        } as AlertContentItem,
        {
          text: `${item.team?.name ?? ""} `,
          type: "bold",
        } as AlertContentItem,
        {
          text: backlogName === "Unprioritized" ? "Unprioritized Backlog" : backlogName,
          type: "regular",
        } as AlertContentItem,
      ]

      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertMessage ?? alertContent,
      })
      if (!onlySend) {
        this.setItem(newItem)
        this.setItemClone(newItem)
      }
      return newItem
    }, true,
    (data) => {
      const errorMessage = data.message.includes(":")
        ? data.message.split(":").slice(1).join(":").trim()
        : data.message
      const alertContent: AlertContentItem[] = [{
        text: errorMessage || "Please try saving the item again.",
        type: "regular",
      }]
      alertModule[AlertActions.SHOW_ERROR_ALERT]({
        contentTitle: [{
          text: "Error Saving the Item.",
          type: "bold",
        }],
        content: alertContent,
        theme: "toast",
      })
    })
  }

  @Action
  async [ItemActions.ITEM_VALIDATE_CREATE_CHECK]({
    payload,
    projectId
  } : {
    payload: ItemValidateCheckPayloadCreate
    projectId?: string
  }) {
    const selectedProject = projectId ?? this.projectId
    const data = await apiService.post<ItemChangeValidationResultDto>(
      Endpoint.ITEM_VALIDATE_CREATE_CHECK(selectedProject),
      payload
    )

    return this.handleResponse<ItemChangeValidationResultDto>(
      data,
      data => new ItemChangeValidationResultDto(data)
    )
  }

  @Action
  async [ItemActions.ITEM_VALIDATE_CHANGE_CHECK]({
    itemId,
    payload,
    projectId
  } : {
    itemId: string
    payload: ItemValidateCheckPayload
    projectId?: string
  }) {
    const selectedProject = projectId ?? this.projectId
    const data = await apiService.post<ItemChangeValidationResultDto>(
      Endpoint.ITEM_VALIDATE_CHANGE_CHECK(this.accountId, itemId, selectedProject),
      payload
    )

    return this.handleResponse<ItemChangeValidationResultDto>(
      data,
      data => new ItemChangeValidationResultDto(data)
    )
  }

  @Action
  async [ItemActions.CHANGE_TEAM_FOR_ITEMS_BATCH](params: { items: Item[], team: Team | null, moveTo?: string, setUnprioritizePriority?: boolean, message?: StructuredDescription }) {
    const payload: {
      itemIds: (string | undefined)[]
      teamId: string
      setUnprioritizePriority: boolean | null
      message?: StructuredDescription
    } = {
      itemIds: params.items.map(i => i.id),
      teamId: params.team?.id ?? "",
      setUnprioritizePriority: params.setUnprioritizePriority ?? null
    }

    if (params.message) {
      payload.message = params.message
    }

    const data = await apiService.post<ResponseToChangingItemTeam[]>(Endpoint.CHANGE_TEAM_FOR_ITEMS_BATCH(this.projectId), payload)

    return this.handleResponse<ResponseToChangingItemTeam[]>(data, (data) => {
      const itemsResponse = data.map(e => new ResponseToChangingItemTeam(e))
      const alertContent = this.getChangeTeamAlert(params)

      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })

      return itemsResponse
    }, true,
    (data) => {
      const errorMessage = data.message.includes(":")
        ? data.message.split(":").slice(1).join(":").trim()
        : data.message
      const alertContent: AlertContentItem[] = [{
        text: errorMessage || "Please try saving the item again.",
        type: "regular",
      }]
      alertModule[AlertActions.SHOW_ERROR_ALERT]({
        contentTitle: [{
          text: "Error Saving the Item.",
          type: "bold",
        }],
        content: alertContent,
        theme: "toast",
      })
    })
  }

  @Action
  async [ItemActions.CHANGE_TEAM_FOR_ITEM](payload: ChangeTeamForItem) {
    const { item, team,  setUnprioritizePriority, message } = payload
    const params: {
      teamId: string | undefined
      setUnprioritizePriority?: boolean
      message?: StructuredDescription
    } = {
      teamId: team?.id,
    };
    if(setUnprioritizePriority){
      params.setUnprioritizePriority = setUnprioritizePriority
    }
    if(message){
      params.message = message
    }
    if (!item.id) return
    const selectedProject = item.projectId ? item.projectId : this.projectId
    const data = await apiService.patch<Item>(Endpoint.UPDATE_TEAM(selectedProject, item.id), {
      ...params
    }, )
    return this.handleResponse<Item>(data, (data) => {
      const newItem = Item.create(data)
      const updateValues = [
        { id: "team", value: newItem.team },
        { id: "teamType", value: newItem.teamType },
        { id: "lastRemovedTeamId", value: newItem.lastRemovedTeamId },
        { id: "owner", value: newItem.owner },
        { id: "tasks", value: newItem.tasks },
        { id: "storyPoints", value: newItem.storyPoints },
        { id: "sprint", value: newItem.sprint },
        { id: "status", value: newItem.status },
        { id: "statusParsed", value: newItem.statusParsed },
        { id: "statusChangeDescription", value: newItem.statusChangeDescription },
      ] as any[]
      this.setItemValues({ payload: updateValues, updateClone: true })

      const alertContent = this.getChangeTeamAlert({
        items: [item],
        team,
        moveTo: "team",
      })
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
      return newItem
    }, true,
    (data) => {
      const alertContent: AlertContentItem[] = [{
        text: `Error: ${data.message.split(":")[1]}`,
        type: "regular",
      }]
      alertModule[AlertActions.SHOW_ERROR_ALERT]({
        content: alertContent,
        theme: "toast",
      })
    })
  }


  @Action
  async [ItemActions.CHANGE_STATUS_ITEM]({ changeStatus } : { changeStatus: ChangeItemStatus }) {
    const projectId = changeStatus.projectId ? changeStatus.projectId : this.projectId
    const data = await apiService.post<StatusChangeInitiator>(Endpoint.CHANGE_STATUS_ITEM(projectId, changeStatus.itemId), {
      message: changeStatus.message,
      status: changeStatus.status,
      todoItem: changeStatus.todoItem,
      fileMetadataIds: changeStatus.fileMetadataIds,
    })
    return this.handleResponse<StatusChangeInitiator>(data, (data) => {
      let toastMessage = ""


      switch (changeStatus.status) {
        case "RE_OPEN":
          toastMessage = "was Re-Opened and moved into Work in Progress."
          break
        case "OBSOLETE":
          toastMessage = "was marked as Obsolete. Use Filter to locate it."
          break
        case "DONE":
          toastMessage = "was marked as Done. Use Filter to locate it."
          break
        default:
          toastMessage = (changeStatus.oldStatus === "OBSOLETE") ? "was removed from Obsolete and moved into Work in Progress." : $t("alert.changeStatus")
          break
      }
      const itemTitle = cutTextByNumberOfSymbols({
        text: changeStatus.itemTitle ?? "",
        numberOfSymbols: 16,
      })
      const alertContent: AlertContentItem[] = [
        {
          text: "Item ",
          type: "regular",
        },
        {
          text: `${itemTitle} `,
          type: "bold",
        },
        {
          text: `${toastMessage}`,
          type: "regular",
        },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        content: alertContent,
        theme: "toast",
        type: AlertType.SUCCESS,
      })
      this.setStatusToItem(data)
      return data
    }, true,
    (data) => {
      const alertContent: AlertContentItem[] = [{
        text: `Error: ${data.message.split(":")[1]}`,
        type: "regular",
      }]
      alertModule[AlertActions.SHOW_ERROR_ALERT]({
        content: alertContent,
        theme: "toast",
      })
    })
  }

  @Action
  async [ItemActions.CHANGE_OWNER_ITEM](changeOwner:ChangeItemOwner) {
    const selectedProject = changeOwner.projectId ? changeOwner.projectId : this.projectId
    const data = await apiService.post<Item>(Endpoint.CHANGE_OWNER_ITEM(selectedProject, changeOwner.itemId), {
      ownerId: changeOwner.ownerId,
    })
    return this.handleResponse<Item>(data, (data) => {
      const itemTitle = cutTextByNumberOfSymbols({
        text: this.item.title ?? "",
        numberOfSymbols: 16,
      })
      const alertContent: AlertContentItem[] = [
        {
          text: "Item ",
          type: "regular",
        },
        {
          text: `${itemTitle} `,
          type: "bold",
        },
        {
          text: $t("alert.changeOwner"),
          type: "regular",
        },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        content: alertContent,
        type: AlertType.SUCCESS,
        theme: "toast",
      })
    }, true,
    (data) => {
      const alertContent: AlertContentItem[] = [{
        text: `Error: ${data.message.split(":")[1]}`,
        type: "regular",
      }]
      alertModule[AlertActions.SHOW_ERROR_ALERT]({
        content: alertContent,
        theme: "toast",
      })
    })
  }

  @Action
  async [ItemActions.UPDATE_ITEM_DETAILS]({ item, notSetItem } : {item: Item, notSetItem?: boolean }) {
    const selectedProject = item.projectId ? item.projectId : this.projectId
    const feContext = item.feContext
    const data = await apiService.put<Item>(Endpoint.UPDATE_ITEM_DETAILS(selectedProject, item.id ?? ""), item.getJsonObj())
    return this.handleResponse<Item>(data, (data) => {
      const alertContent = this.getItemUpdateAlertContent(item)

      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
      const itemCreate = Item.create(data)

      if (!notSetItem) {
        this.setItem(itemCreate)
        this.item.setInitialState()
      } else {
        this.setItemValues({
          payload: [
            {
              id: "backlogPriority",
              value: itemCreate.backlogPriority,
            },
            {
              id: "sprintPriority",
              value: itemCreate.sprintPriority,
            },
            {
              id: "sprint",
              value: itemCreate.sprint,
            },
            {
              id: "definitionsOfDone",
              value: itemCreate.definitionsOfDone ?? [],
            },
            {
              id: "feContext",
              value: feContext
            }

          ] as unknown as UpdateObjectValue<Item>[],
          updateClone: true,
        })
      }
      // Sync the DoD list with the BE response on both paths. Dispatched via the global store rather
      // than importing definitionOfDoneModule directly to avoid a static import cycle.
      store.dispatch(`${Modules.DEFINITION_OF_DONE}/UPDATE_DEFINITION_OF_DONE_LIST`, itemCreate.definitionsOfDone ?? [])
      store.dispatch(`${Modules.DEFINITION_OF_DONE}/RESET_ALL_DEFINITIONS`)
     
      return itemCreate
    }, true,
    (data) => {
      const errorMessage = data.message.includes(":")
        ? data.message.split(":").slice(1).join(":").trim()
        : data.message
      const alertContent: AlertContentItem[] = [{
        text: errorMessage || "Please try saving the item again.",
        type: "regular",
      }]
      alertModule[AlertActions.SHOW_ERROR_ALERT]({
        contentTitle: [{
          text: "Error Saving the Item.",
          type: "bold",
        }],
        content: alertContent,
        theme: "toast",
      })
    })
  }

  @Action
  async [ItemActions.UPDATE_TEAM]({ itemId, projectId, teamId, setUnprioritizePriority, message } : {itemId: string, projectId?: string, teamId: string, setUnprioritizePriority: boolean, message?: StructuredDescription}) {
    const payload: {teamId: string, setUnprioritizePriority?: boolean, message?: StructuredDescription} = {
      teamId: teamId,
    }

    if(setUnprioritizePriority){
      payload.setUnprioritizePriority = setUnprioritizePriority
    }
    if(message){
      payload.message = message
    }
    const selectedProject = projectId ? projectId : this.projectId
    const data = await apiService.patch<Item>(Endpoint.UPDATE_TEAM(selectedProject, itemId), {
      ...payload
    })
    return this.handleResponse<Item>(data, data => new Item(data))
  }

  @Action
  async [ItemActions.UPDATE_ITEM_TEAM_TYPE]({
    itemId,
    projectId,
    teamType
  } : {
    itemId: string
    projectId?: string
    teamType: TeamType
  }) {
    const selectedProject = projectId ? projectId : this.projectId
    const data = await apiService.patch<Item>(
      Endpoint.UPDATE_ITEM_TEAM_TYPE(selectedProject, itemId),
      { teamType }
    )
    return this.handleResponse<Item>(data, data => {
      const item = Item.create(data)
      const alertContent: AlertContentItem[] = [
        {
          text: "Domain was updated",
          type: "regular",
        },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        content: alertContent,
        type: AlertType.SUCCESS,
        theme: "toast",
      })
      return item
    })
  }

  @Action
  async [ItemActions.GET_ITEM_FILES]({ itemId, projectId, filterFiles }: { itemId: string, projectId?: string, filterFiles }) {
    const filter = filterFiles?.getJsonObj()
    const selectedProject = projectId ? projectId : this.projectId
    const data = await apiService.get<ServerResponse<FileMetaData>>(Endpoint.GET_ITEM_FILES(this.accountId, selectedProject, itemId), filter)
    return this.handleResponse<ServerResponse<FileMetaData>>(data, (data) => {
      const content = data.content.map((file: FileMetaData) => FileMetaData.create(file))
      return {...data, content}
    })
  }

  @Action
  async [ItemActions.UPDATE_DEFINITION_OF_DONE](payload: ItemDefinitionPyload) {
    const selectedProject = payload.projectId ? payload.projectId : this.projectId
    const data = await apiService.patch<void>(Endpoint.ITEM_DEFINITION_OF_DONE(selectedProject, payload.itemId, payload.definitionOfDoneId))
    return this.handleResponse<void>(data)
  }

  @Action
  async [ItemActions.ADD_ITEM_TO_EPIC](payload: AddItemEpicPayload) {
    const { itemId, epicId } = payload
    let itemIds: string[] = []

    itemIds = typeof itemId === "string" ? [itemId] : itemId

    const data = await apiService.post<void>(Endpoint.ASSIGN_EPIC(this.projectId, epicId), itemIds)
    return this.handleResponse<void>(data)
  }

  @Action
  async [ItemActions.GET_ITEMS_SNAPSHOT_BY_EPIC_V2](
    {
      epicId,
      milestoneId,
      projectId,
      filter
    }: { epicId: string, milestoneId: string, projectId?: string, filter: FilterBacklog }
  ) {
    const selectedProject = projectId ?? this.projectId
    const data = await apiService.get<ServerResponse<Item>>(
      Endpoint.GET_ITEMS_SNAPSHOT_BY_EPIC_V2(selectedProject, epicId, milestoneId),
      filter.getJsonObj()
    )
    return this.handleResponse<ServerResponse<Item>>(data, data => {
      const items = data.content.map(i => Item.create(i))
      return items
    })
  }

  @Action
  async [ItemActions.DELETE_ITEM]({ item }: { item: Item }) {
    const data = await apiService.delete<Item>(Endpoint.MARK_AS_DELETED(this.projectId, item.id ?? ""))
    return this.handleResponse<Item>(data, () => {
      const alertContent: AlertContentItem[] = [
        {
          text: `${item.itemTypeParsed.label} #${item.sequenceNumber} `,
          type: "bold",
        },
        {
          text: 'deleted',
          type: "regular",
        },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        content: alertContent,
        type: AlertType.SUCCESS,
        theme: "toast",
      })
      this.setItemValue({ id: "isDeleted", value: true } as any)
    }, true,
    (data) => {
      const alertContent: AlertContentItem[] = [{
        text: `Error: ${data.message.split(":")[1]}`,
        type: "regular",
      }]
      alertModule[AlertActions.SHOW_ERROR_ALERT]({
        content: alertContent,
        theme: "toast",
      })
    })
  }

  @Action
  async [ItemActions.DELETE_ITEM_FILE]({ itemId, fileMetadataId, projectId } : { itemId: string, fileMetadataId: string, projectId?: string }) {
    const selectedProjectId = projectId ?? this.projectId
    const data = await apiService.delete<Item>(Endpoint.DELETE_ITEM_FILE(selectedProjectId, itemId, fileMetadataId))
    return this.handleResponse<Item>(data, () => {
      this.removeFileFromItem(fileMetadataId)
      const alertContent: AlertContentItem[] = [{
        text: `File ${$t("alert.deleted")}`,
        type: "regular",
      }]
      alertModule[AlertActions.SHOW_ALERT]({
        content: alertContent,
        type: AlertType.SUCCESS,
        theme: "toast",
      }), true,
      (data) => {
        return data
      }
    })
  }

  @Action
  async [ItemActions.MOVE_TO_ITEM]({ itemId, projectId, moveTo }:{itemId: string, projectId?: string, moveTo: MoveTo}) {
    const selectedProject = projectId ? projectId : this.projectId
    const data = await apiService.post<MoveTo>(Endpoint.MOVE_TO_ITEM(selectedProject, itemId), moveTo.getJsonObj())
    return this.handleResponse<MoveTo>(data, () => {
      const moveToLabel = moveTo.moveToLabel
      const alertContent: AlertContentItem[] = [
        {
          text: "Task ",
          type: "regular",
        },
        {
          text: `"${moveTo.title}" `,
          type: "bold",
        } as AlertContentItem,
        {
          text: "moved to ",
          type: "regular",
        } as AlertContentItem,
        {
          text: `${moveToLabel} `,
          type: "bold",
        } as AlertContentItem,
        {
          text: "as a new ",
          type: "regular",
        } as AlertContentItem,
        {
          text: `${moveTo.itemTypeParsed.label} `,
          type: "bold",
        } as AlertContentItem,
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
    }, true,
    (data) => {
      const alertContent: AlertContentItem[] = [{
        text: `Error: ${data.message.split(":")[1]}`,
        type: "regular",
      }]
      alertModule[AlertActions.SHOW_ERROR_ALERT]({
        content: alertContent,
        theme: "toast",
      })
    })
  }

  @Action
  async [ItemActions.DUPLICATE_ITEM]({ itemId, projectId }:{itemId: string, projectId?: string }) {
    const selectedProject = projectId ? projectId : this.projectId
    const data = await apiService.post<Item>(Endpoint.DUPLICATE_ITEM(selectedProject, itemId))
    return this.handleResponse<Item>(data, (data) => {
      const item = Item.create(data)
      const backlogName = Item.getBacklogNameByItem(item)
      const alertContent: AlertContentItem[] = [
        {
          text: `${item.itemTypeParsed?.label ?? "Story"} `,
          type: "regular",
        },
        {
          text: `#${item.sequenceNumber} `,
          type: "bold",
        },
        { 
          text: `was added to ${backlogName === "Unprioritized" ? "Unprioritized Backlog" : backlogName}`, 
          type: "regular" 
        }
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
        routerLink: {
          name: "Item",
          params: { itemId: data.sequenceNumber.toString() },
          title: "Open Item"
        },
        timeout: 60000,
      })
      return item
    })
  }

  @Action
  [ItemActions.UPDATE_ITEM_VALUE](payload: UpdateObjectValue<Item>) {
    this.setItemValue(payload)
  }

  @Action
  [ItemActions.UPDATE_ITEM_CLONE_VALUE](payload: UpdateObjectValue<Item>) {
    this.setItemCloneValue(payload)
  }

  @Action
  [ItemActions.UPDATE_ITEM_VALUES]({ payload, updateClone } : { payload: UpdateObjectValue<Item>[], updateClone?: boolean }) {
    this.setItemValues({ payload, updateClone })
  }

  @Action
  [ItemActions.SET_ITEM](item: Item) {
    this.setItem(item)
  }

  @Action
  [ItemActions.RESET_ITEM]() {
    this.resetItem()
  }

  @Action
  [ItemActions.PUT_ITEM](item: Item) {
    this.setItem(Item.create(item))
    this.setItemClone(Item.create(item))

    if (this.items.map(i => i.id).includes(item?.id)) {
      this.replaceItem(Item.create(item))
    }
  }

  @Action
  [ItemActions.UPDATE_ITEM_CLONE](itemField: string) {
    this.updateItemClone(itemField)
  }

  @Action
  [ItemActions.REMOVE_DEFINITION](taskType: TaskType) {
    this.removeDefinition(taskType)
  }

  @Action
  [ItemActions.UPDATE_ITEM_STATUS_IN_LIST]({ status, itemId } : {status: ItemStatus, itemId: string}) {
    this.setStatusToItemList({ status, itemId })
  }

  @Action
  async [ItemActions.RESET_CLONE]() {
    this.resetItemClone()
  }

  @Action
  async [ItemActions.UPDATE_ITEM_IN_LIST]({ payload, itemId } : { payload: UpdateObjectValue<Item>, itemId: string}) {
    this.setItemInList({ payload, itemId })
  }

  @Action
  async [ItemActions.GET_BACKLOG_CREATED_ITEM](item: Item | null) {
    this.setbacklogCreatedItem(item)
  }

  @Action
  async [ItemActions.UPDATE_VALUE_FOR_BACKLOG_CREATED_ITEM](params: UpdateObjectValue<Item>) {
    this.setValueForBacklogCreatedItem(params)
  }

  @Action
  async [ItemActions.REMOVE_FILE_FROM_ITEM](attachmentId: string) {
    this.removeFileFromItem(attachmentId)
  }

  @Action
  async [ItemActions.UPDATE_ITEM_READINESS](payload) {
    const data = await apiService.patch<boolean>(Endpoint.UPDATE_ITEM_READINESS(this.projectId, payload.item.id), undefined, {
      params: { ready: payload.ready },
    })
    return this.handleResponse<boolean>(data, (data) => {
      const alertContent: AlertContentItem[] = [
        {
          text: `${payload.item.itemTypeParsed?.label ?? ""}`,
          type: "bold",
        } as AlertContentItem,
        {
          text: ` #${payload.item.sequenceNumber}`,
          type: "bold",
        },
        {
          text: `  is marked as${data ? " " : " not "}Ready`,
          type: "regular",
        } as AlertContentItem,
      ]

      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
    })
  }
}

export const itemModule = new ItemModule({ store, name: Modules.ITEM })
